import React, { useEffect, useRef, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';

import { Stack, xcss, Box } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';
import { getCommentPositionTopOfSection } from '@confluence/comments-util';
import { ReactionsProvider } from '@confluence/comment-context';
import { withErrorBoundary, Attribution } from '@confluence/error-boundary';
import { withFlags } from '@confluence/flags';
import type { WithFlagsProps } from '@confluence/flags';
import { usePageContentId } from '@confluence/page-context';
import { useCommentsData, useActiveCommentsQuery } from '@confluence/comments-data';
import {
	useCommentsPanel,
	ViewValues,
	getOpenCommentThreads,
	LocationValue,
} from '@confluence/comments-panel-utils';
import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder';

import { ObjectAddCommentContainer } from './ObjectAddCommentContainer';
import { ObjectCommentsList } from './ObjectCommentsList';
import { ObjectCommentHeader } from './ObjectCommentHeader';

const commentPanelContainerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	height: '100%',
	width: '100%',
});

const placeholderStyle = xcss({
	position: 'absolute',
	bottom: 'space.0',
	width: '60%',
});

const addCommentContainerStyles = xcss({
	transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
	transformOrigin: 'top',
	position: 'sticky',
	bottom: 'space.0',
});

const addCommentContainerAnimationHiddenStyles = xcss({
	transform: 'translateY(100%)',
	opacity: 0,
	pointerEvents: 'none',
});

const addCommentContainerAnimationShowStyles = xcss({
	transform: 'translateY(0)',
	opacity: 1,
	pointerEvents: 'auto',
});

type CommentsQueryHookVariables = {
	pageId: string;
};

const ObjectCommentContainerComponent = withFlags(({ flags }: WithFlagsProps) => {
	const [contentId] = usePageContentId();

	const pageContainsBodiedMacrosRef = useRef(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [commentSectionTop, setCommentSectionTop] = useState<number | undefined>(undefined);

	const [
		{ orderedActiveAnnotationIdList, commentsDataMap, removedThreadsMap },
		{ clearRemovedComments },
	] = useCommentsData();
	const [
		{ initialDataLoadedForViewMap, showInlineComments, showGeneralComments, replyToCommentId },
		{ setInitialDataLoadedForView },
	] = useCommentsPanel();
	const isInitialCommentDataLoaded = initialDataLoadedForViewMap[ViewValues.OPEN];

	const commentsQueryHook = useActiveCommentsQuery;
	const commentsQueryHookVariables: CommentsQueryHookVariables = {
		pageId: contentId || '',
	};

	const { loading, error } = commentsQueryHook(commentsQueryHookVariables);

	// We need to check that there are page properties macros or excerpt macros on the page
	// so we can hide the delete option which causes weird issues in the document
	useEffect(() => {
		const bodiedMacroElem = document.querySelector('.ak-renderer-extension');
		pageContainsBodiedMacrosRef.current = Boolean(bodiedMacroElem);
	}, []);

	useEffect(() => {
		// When this view unmounts, we want to clear out the "resolved"/"deleted" comments
		return () => {
			clearRemovedComments();
		};
	}, [clearRemovedComments]);

	useEffect(() => {
		if (!loading) {
			setInitialDataLoadedForView({ viewToSetLoaded: ViewValues.OPEN });
		}
	}, [loading, setInitialDataLoadedForView]);

	const commentThreads = useMemo(() => {
		if (
			(!isInitialCommentDataLoaded && loading) ||
			(orderedActiveAnnotationIdList.length === 0 && Object.keys(removedThreadsMap).length === 0)
		) {
			return [];
		}

		return getOpenCommentThreads({
			commentsDataMap,
			orderedActiveAnnotationIdList,
			removedThreadsMap,
			showInlineComments,
			showGeneralComments,
			location: LocationValue.FOOTER,
		});
	}, [
		loading,
		isInitialCommentDataLoaded,
		commentsDataMap,
		orderedActiveAnnotationIdList,
		removedThreadsMap,
		showInlineComments,
		showGeneralComments,
	]);

	useEffect(() => {
		const editorTextArea = document.getElementById('ak-editor-textarea');

		if (editorTextArea) {
			// Create a ResizeObserver to watch for size changes
			const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
				if (fg('confluence_footer_comment_styling_change')) {
					const newTop = getCommentPositionTopOfSection(entries);
					// Warning temporary to unblock modernisation release (see COMMENTS-3668)
					if (fg('confluence_footer_comment_styling_fix_flicker')) {
						flushSync(() => {
							newTop && setCommentSectionTop(newTop);
						});
					} else {
						newTop && setCommentSectionTop(newTop);
					}
				}
			});
			// Start observing the editorTextArea for size changes
			resizeObserver.observe(editorTextArea);
			// Cleanup function to disconnect the observer on unmount
			return () => resizeObserver.disconnect();
		}
	}, []);

	if (!isInitialCommentDataLoaded) {
		if (loading) {
			return (
				<Box xcss={placeholderStyle}>
					<CommentPlaceholder showPlaceholder />;
				</Box>
			);
		}

		if (error) {
			return null;
		}
	}

	const showDeleteOption =
		fg('confluence_frontend_nested_renderer_delete') || !pageContainsBodiedMacrosRef.current;

	const hasComments = commentThreads?.length != 0;

	return (
		<Box
			ref={wrapperRef}
			data-test-id="object-comment-wrapper"
			/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
			style={{
				position: 'relative',
				top: commentSectionTop,
				visibility: loading ? 'hidden' : 'visible',
				width: '100%',
			}}
			/* eslint-enable @atlaskit/ui-styling-standard/enforce-style-prop */
		>
			<ReactionsProvider contentId={contentId ?? ''}>
				{hasComments && <ObjectCommentHeader />}
				<Stack xcss={commentPanelContainerStyles}>
					<ObjectCommentsList
						commentThreads={commentThreads}
						supportedTopLevelActions={
							showDeleteOption ? ['edit', 'resolve', 'delete'] : ['edit', 'resolve']
						}
						flags={flags}
					/>
				</Stack>
				<Box
					xcss={[
						addCommentContainerStyles,
						!!replyToCommentId
							? addCommentContainerAnimationHiddenStyles
							: addCommentContainerAnimationShowStyles,
					]}
				>
					<ObjectAddCommentContainer showReactions={!hasComments} />
				</Box>
			</ReactionsProvider>
		</Box>
	);
});

export const ObjectCommentContainer = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(ObjectCommentContainerComponent);
